/**
 * Os identificadores externos de uma embalagem: o **GTIN** (o código de barras)
 * e o **ASIN** (o da Amazon).
 *
 * ## UPC e EAN NÃO são códigos diferentes, e tratá-los como se fossem racha o catálogo
 *
 * UPC-A tem 12 dígitos, EAN-13 tem 13, e o UPC-A **é** o EAN-13 sem o zero da
 * frente: `012345678905` e `0012345678905` são a mesma embalagem, na mesma
 * gôndola. Guardar os dois em campos separados faria a mesma Coca casar pelo
 * campo A e não casar pelo campo B — que é, um nível abaixo, o mesmo erro que
 * `ProductMatch.md` chama de *"o erro clássico que arruína catálogos"*.
 *
 * A saída é a que o próprio GS1 usa: **normalizar para GTIN-14**, preenchendo
 * com zeros à esquerda. EAN-8, UPC-A, EAN-13 e GTIN-14 viram uma forma só, e a
 * comparação passa a ser entre iguais.
 *
 * ## O dígito verificador é conferido, pelo mesmo motivo da chave da NFC-e
 *
 * Um código com um dígito trocado é **sintaticamente perfeito**: tem o
 * comprimento certo e só dígitos. Sem o módulo 10, ele entra no catálogo e ou
 * não casa com nada para sempre, ou — pior — casa com outro produto. É a mesma
 * armadilha que `nfce-key.ts` já fecha, e a mesma resposta.
 *
 * **Código que não fecha NÃO é recusado**: ele fica guardado como veio, em
 * `Product.barcode`, e apenas não vira `gtin`. Recusar o cadastro seria hostil,
 * e códigos internos de mercado existem. O que não pode é ele casar com alguma
 * coisa por acidente.
 *
 * ## ASIN é outra coisa, e não entra no casamento de linha
 *
 * O ASIN é identificador **da Amazon**: dez caracteres, não vem impresso na
 * embalagem e nunca aparece num cupom fiscal. Ele serve para chegar na página do
 * produto — e é por isso que a tela o transforma num link. Deixá-lo participar
 * da cascata de `resolveLine` seria procurar, numa nota fiscal, um código que
 * nota fiscal não tem.
 */

/** O comprimento canônico. GS1 chama de GTIN-14. */
const TAMANHO = 14

/**
 * O código de barras normalizado, ou `null` quando não é um GTIN válido.
 *
 * Aceita EAN-8, UPC-A (12), EAN-13 e GTIN-14, com ou sem espaços e hífens —
 * que é como alguém digita olhando a embalagem.
 */
export function gtinOf(raw: string | null): string | null {
  if (raw === null) return null
  const digitos = raw.replace(/\D/g, '')
  // Os quatro comprimentos que o GS1 define. Qualquer outro é digitação pela
  // metade, e completar com zeros um código incompleto inventaria um produto.
  if (![8, 12, 13, 14].includes(digitos.length)) return null

  const canonico = digitos.padStart(TAMANHO, '0')
  return checkDigitOk(canonico) ? canonico : null
}

/**
 * O módulo 10 do GS1: pesos 3 e 1 alternados **da direita para a esquerda**,
 * a partir do dígito à esquerda do verificador.
 *
 * Inverter o sentido produz um algoritmo que valida um conjunto diferente de
 * códigos, todos errados — mesma observação de `nfce-key.ts`.
 */
export function checkDigitOk(gtin: string): boolean {
  if (!/^\d{14}$/.test(gtin)) return false

  let soma = 0
  for (let i = 12; i >= 0; i -= 1) {
    const posicaoDaDireita = 13 - i
    soma += Number(gtin[i]) * (posicaoDaDireita % 2 === 1 ? 3 : 1)
  }
  const dv = (10 - (soma % 10)) % 10
  return dv === Number(gtin[13])
}

/**
 * O ASIN normalizado, ou `null`.
 *
 * Dez caracteres alfanuméricos, em maiúsculas — `b08n5wrwnw` e `B08N5WRWNW` são
 * o mesmo produto, e guardar as duas formas faria duas buscas diferentes.
 * Aceita a URL inteira da Amazon, que é o que se tem na mão quando se está
 * olhando o produto lá.
 */
export function asinOf(raw: string | null): string | null {
  if (raw === null) return null
  const limpo = raw.trim().toUpperCase()
  if (limpo === '') return null

  // `/dp/B08N5WRWNW`, `/gp/product/B08N5WRWNW`, ou o código sozinho.
  const daUrl = /(?:\/DP\/|\/GP\/PRODUCT\/)([A-Z0-9]{10})/.exec(limpo)
  const candidato = daUrl?.[1] ?? limpo
  return /^[A-Z0-9]{10}$/.test(candidato) ? candidato : null
}

/** A página do produto na Amazon. É o que faz o campo valer alguma coisa hoje. */
export function amazonUrl(asin: string): string {
  return `https://www.amazon.com.br/dp/${asin}`
}
